import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { StatusPill } from '../../components/StatusPill';
import { Modal } from '../../components/Modal';
import { LocationPicker, LocationPreview } from '../../components/LocationPicker';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';

type ShopType = 'HQ' | 'SHOP' | 'ONLINE';

interface Shop {
  id: string;
  code: string;
  name: string;
  type: ShopType;
  isHeadOffice: boolean;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  phone: string | null;
  invoiceCounter: number;
  invoicePrefix: string | null;
  isActive: boolean;
}

/** Compact row showing the pinned location with a "change" affordance. */
function LocationField({
  lat,
  lng,
  address,
  onChange,
}: {
  lat: number | null;
  lng: number | null;
  address: string;
  onChange: (loc: { lat: number; lng: number; address: string }) => void;
}): JSX.Element {
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasPin = lat != null && lng != null;
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-medium text-ink-3 uppercase tracking-wide">
          Map location &amp; address search
        </label>
        <Button type="button" variant="tertiary" size="xs" onClick={() => setPickerOpen(true)}>
          {hasPin ? 'Change' : '🔍 Search address'}
        </Button>
      </div>
      {hasPin ? (
        <div className="space-y-1">
          <LocationPreview lat={lat!} lng={lng!} height={120} />
          <div className="text-[10px] text-ink-3 kc-mono">
            {lat!.toFixed(6)}, {lng!.toFixed(6)}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="w-full border border-dashed border-rule rounded-md py-6 text-xs text-ink-3 hover:bg-paper-2 hover:border-accent/40 transition"
        >
          🔍 Search an address (auto-fills below) or drop a pin on the map
        </button>
      )}
      <LocationPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initial={{ lat, lng, address }}
        onConfirm={(loc) => {
          onChange(loc);
          setPickerOpen(false);
        }}
      />
    </div>
  );
}

export function ShopsPage(): JSX.Element {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Shop | null>(null);
  const shops = useQuery({
    queryKey: ['shops-admin'],
    queryFn: () => apiGet<Shop[]>('/shops', { includeInactive: 'true' }),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => apiPost(`/shops/${id}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shops-admin'] }),
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => apiPatch(`/shops/${id}`, { isActive: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shops-admin'] }),
  });

  return (
    <PageScaffold
      scroll={false}
      title="Shops"
      subtitle={`${(shops.data ?? []).length} location${(shops.data ?? []).length === 1 ? '' : 's'} · physical + HQ counter.`}
      actions={<Button onClick={() => setCreateOpen(true)}>+ Add shop</Button>}
    >
      <DataTableCard>
        <table className="w-full text-sm">
          <thead className={STICKY_HEAD}>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
              <th className="px-6 py-2 font-medium">Code</th>
              <th className="px-6 py-2 font-medium">Name</th>
              <th className="px-6 py-2 font-medium">Type</th>
              <th className="px-6 py-2 font-medium">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(shops.data ?? []).map((s) => (
              <tr key={s.id} className="border-b border-rule-2 group hover:bg-paper-2">
                <td className="px-6 py-3">
                  <span className="kc-mono">{s.code}</span>
                </td>
                <td className="px-6 py-3 text-ink">{s.name}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-1.5">
                    <StatusPill
                      tone={s.type === 'HQ' ? 'accent' : s.type === 'ONLINE' ? 'info' : 'neutral'}
                    >
                      {s.type === 'HQ' ? 'HQ' : s.type === 'ONLINE' ? 'Online' : 'Shop'}
                    </StatusPill>
                    {s.isHeadOffice && <StatusPill tone="accent">Head office</StatusPill>}
                  </div>
                </td>
                <td className="px-6 py-3">
                  <StatusPill tone={s.isActive ? 'positive' : 'neutral'}>
                    {s.isActive ? 'active' : 'inactive'}
                  </StatusPill>
                </td>
                <td className="px-6 py-3 text-right">
                  <div className="flex gap-1 justify-end opacity-60 group-hover:opacity-100 transition">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(s)}>
                      Edit
                    </Button>
                    {s.isActive ? (
                      <Button size="sm" variant="ghost" onClick={() => deactivate.mutate(s.id)}>
                        Deactivate
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reactivate.mutate(s.id)}>
                        Reactivate
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataTableCard>

      {createOpen && <CreateShopModal onClose={() => setCreateOpen(false)} />}
      {editing && <EditShopModal shop={editing} onClose={() => setEditing(null)} />}
    </PageScaffold>
  );
}

function CreateShopModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<ShopType>('SHOP');
  const [isHeadOffice, setIsHeadOffice] = useState(false);
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [phone, setPhone] = useState('');
  const isOnline = type === 'ONLINE';
  const isShop = type === 'SHOP';
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: () =>
      apiPost('/shops', {
        code: code.toUpperCase(),
        name,
        type,
        isHeadOffice: isShop ? isHeadOffice : undefined,
        address: isOnline ? undefined : address || undefined,
        latitude: isOnline ? undefined : (latitude ?? undefined),
        longitude: isOnline ? undefined : (longitude ?? undefined),
        phone: phone || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shops-admin'] });
      qc.invalidateQueries({ queryKey: ['stock-locations'] });
      onClose();
    },
  });

  return (
    <Modal
      title="New shop"
      subtitle="A new stock location is created automatically."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submit.isPending}
            disabled={!code || !name}
            onClick={() => submit.mutate()}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Code (uppercase, e.g. GS)"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={8}
          required
        />
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Type</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ShopType)}
            className="kc-input"
          >
            <option value="SHOP">Showroom (physical shop)</option>
            <option value="HQ">HQ (legacy head-office node — no stock)</option>
            <option value="ONLINE">Online (web channel — no stock location)</option>
          </select>
        </label>
        {isShop && (
          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={isHeadOffice}
              onChange={(e) => setIsHeadOffice(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Head-office counter
              <span className="block text-xs text-ink-3">
                HQ-level staff default to selling here, and everyone (not just sales girls) earns a
                commission share. Only one shop should be the head office.
              </span>
            </span>
          </label>
        )}
        {!isOnline && (
          <>
            <Input
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              hint="Type it, or use 'Search address' below to look it up and auto-fill."
            />
            <LocationField
              lat={latitude}
              lng={longitude}
              address={address}
              onChange={({ lat, lng, address: addr }) => {
                setLatitude(lat);
                setLongitude(lng);
                if (addr) setAddress(addr);
              }}
            />
          </>
        )}
        <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}

function EditShopModal({ shop, onClose }: { shop: Shop; onClose: () => void }): JSX.Element {
  const [name, setName] = useState(shop.name);
  const [address, setAddress] = useState(shop.address ?? '');
  const [latitude, setLatitude] = useState<number | null>(shop.latitude);
  const [longitude, setLongitude] = useState<number | null>(shop.longitude);
  const [phone, setPhone] = useState(shop.phone ?? '');
  const [invoicePrefix, setInvoicePrefix] = useState(shop.invoicePrefix ?? '');
  const [isHeadOffice, setIsHeadOffice] = useState(shop.isHeadOffice);
  const isShop = shop.type === 'SHOP';
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: () =>
      apiPatch(`/shops/${shop.id}`, {
        name,
        address: address || null,
        latitude,
        longitude,
        phone: phone || null,
        invoicePrefix: invoicePrefix || null,
        ...(isShop ? { isHeadOffice } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shops-admin'] });
      qc.invalidateQueries({ queryKey: ['shops'] });
      onClose();
    },
  });
  return (
    <Modal
      title={`Edit · ${shop.code}`}
      subtitle="Shop code can't be changed — it's baked into invoice numbers."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={submit.isPending} disabled={!name} onClick={() => submit.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          hint="Type it, or use 'Search address' below to look it up and auto-fill."
        />
        <LocationField
          lat={latitude}
          lng={longitude}
          address={address}
          onChange={({ lat, lng, address: addr }) => {
            setLatitude(lat);
            setLongitude(lng);
            if (addr) setAddress(addr);
          }}
        />
        <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        {isShop && (
          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={isHeadOffice}
              onChange={(e) => setIsHeadOffice(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              Head-office counter
              <span className="block text-xs text-ink-3">
                HQ-level staff default to selling here, and everyone earns a commission share. Only
                one shop should be the head office.
              </span>
            </span>
          </label>
        )}
        <Input
          label="Invoice prefix"
          value={invoicePrefix}
          onChange={(e) => setInvoicePrefix(e.target.value)}
          placeholder={`KBW-${shop.code}`}
          hint={`Current issued invoices: ${shop.invoiceCounter}`}
        />
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}
